import React from 'react';
import { Handle, Position } from 'reactflow';
import { Wand2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui';

// Nested format from MCP server
interface NestedTransformData {
  inputExpression?: string;
  transformType?: 'parse_json' | 'stringify' | 'extract' | 'map' | 'filter' | 'custom';
  customTransform?: string;
  extractPath?: string;
  outputVariable?: string;
}

interface TransformNodeData {
  label?: string;
  // Support both flat format and nested format (transform: { ... })
  transform?: NestedTransformData;
  // Flat format fields (legacy)
  inputExpression?: string;
  transformType?: string;
  extractPath?: string;
  outputVariable?: string;
}

// Helper to get transform data from either format
function getTransformData(data: TransformNodeData): NestedTransformData {
  // Prefer nested format
  if (data.transform) {
    return data.transform;
  }
  // Fall back to flat format
  return {
    inputExpression: data.inputExpression,
    transformType: data.transformType as NestedTransformData['transformType'],
    extractPath: data.extractPath,
    outputVariable: data.outputVariable,
  };
}

// Get display label for transform type
function getTransformTypeLabel(type?: string): string {
  const labels: Record<string, string> = {
    parse_json: 'Parse JSON',
    stringify: 'Stringify',
    extract: 'Extract',
    map: 'Map',
    filter: 'Filter',
    custom: 'Custom',
  };
  return labels[type || ''] || type || 'Transform';
}

// Get badge variant for transform type
function getTransformBadgeVariant(type?: string): 'default' | 'secondary' | 'outline' {
  switch (type) {
    case 'extract':
      return 'default';
    case 'parse_json':
    case 'stringify':
      return 'secondary';
    default:
      return 'outline';
  }
}

export default function TransformNode({
  data,
  selected,
}: {
  data: TransformNodeData;
  selected?: boolean;
}) {
  const transformData = getTransformData(data);

  return (
    <div
      className={cn(
        'min-w-[160px] rounded-lg',
        'bg-card',
        'border-2 transition-all duration-fast',
        selected
          ? 'border-accent-purple shadow-glow-focus'
          : 'border-accent-purple/50 dark:border-accent-purple/30 shadow-md hover:shadow-lg'
      )}
    >
      {/* Connection Handle */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !border-2 !bg-accent-purple !border-accent-purple"
        style={{ top: '-6px' }}
      />

      {/* Node Header */}
      <div className="flex items-center gap-2 p-3 border-b border-border dark:border-border-subtle">
        <div className="w-6 h-6 rounded bg-accent-purple/10 dark:bg-accent-purple/20 flex items-center justify-center">
          <Wand2 className="w-3.5 h-3.5 text-accent-purple" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-foreground">
            {data.label || 'Transform'}
          </div>
          <div className="text-xs text-muted-foreground/70">
            Data transformation
          </div>
        </div>
        {transformData.transformType && (
          <Badge variant={getTransformBadgeVariant(transformData.transformType)}>
            {getTransformTypeLabel(transformData.transformType)}
          </Badge>
        )}
      </div>

      {/* Node Body */}
      <div className="p-3 space-y-2">
        {/* Input Expression */}
        {transformData.inputExpression && (
          <div className="text-xs">
            <span className="text-muted-foreground font-medium block mb-1">Input:</span>
            <span className="text-foreground font-mono text-xs block truncate">
              {transformData.inputExpression}
            </span>
          </div>
        )}

        {/* Extract Path (for extract type) */}
        {transformData.transformType === 'extract' && transformData.extractPath && (
          <div className="text-xs">
            <span className="text-muted-foreground font-medium block mb-1">Path:</span>
            <span className="text-foreground font-mono text-xs block truncate">
              {transformData.extractPath}
            </span>
          </div>
        )}

        {/* Output Variable */}
        {transformData.outputVariable && (
          <div className="text-xs pt-2 border-t border-border dark:border-border-subtle">
            <span className="text-muted-foreground font-medium">Output: </span>
            <span className="text-accent-purple font-mono">${transformData.outputVariable}</span>
          </div>
        )}

        {/* Not configured state */}
        {!transformData.inputExpression && !transformData.outputVariable && (
          <div className="text-xs text-muted-foreground/70 italic">
            Configure in properties
          </div>
        )}
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !border-2 !bg-accent-purple !border-accent-purple"
        style={{ bottom: '-6px' }}
      />
    </div>
  );
}
